"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, ProductPrice, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wpQueryParams = {
  product_cat: "16",
  per_page: 12,
  order: "desc",
  orderby: "date"
};
const wpQuery = new WP_Query(wpQueryParams);
function ProductCardImage({
  product
}: {
  product: any;
}) {
  const images: string[] = product.gallery_image_urls && product.gallery_image_urls.length > 0 ? product.gallery_image_urls : product.featured_image_url ? [product.featured_image_url] : [];
  const [currentIdx, setCurrentIdx] = React.useState(0);
  const hasMultiple = images.length > 1;
  const currentImage = images[currentIdx] || product.featured_image_url;
  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIdx(prev => (prev - 1 + images.length) % images.length);
  };
  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIdx(prev => (prev + 1) % images.length);
  };
  return <div className="relative aspect-[16/10] w-full overflow-hidden border-b border-border bg-muted p-4">
      {currentImage ? <img src={currentImage} alt={product.title} loading="lazy" className="h-full w-full object-contain grayscale-[20%] transition-transform duration-300 group-hover:scale-[1.02]" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
          <FileText className="h-10 w-10 stroke-1" />
        </div>}

      {hasMultiple && <>
          <Button type="button" variant="outline" size="icon" onClick={handlePrev} aria-label="Previous image" className="absolute left-2 top-1/2 -translate-y-1/2 size-7 rounded-none border border-border bg-background/90 text-foreground hover:bg-foreground hover:text-background transition-colors z-10 p-0 shadow-xs">
            <ChevronLeft className="size-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" onClick={handleNext} aria-label="Next image" className="absolute right-2 top-1/2 -translate-y-1/2 size-7 rounded-none border border-border bg-background/90 text-foreground hover:bg-foreground hover:text-background transition-colors z-10 p-0 shadow-xs">
            <ChevronRight className="size-4" />
          </Button>
        </>}

      <div className="absolute top-3 left-3">
        <Badge className="rounded-none border border-border bg-foreground px-2.5 py-0.5 font-mono text-[11px] font-medium tracking-wider text-background uppercase">
          NEW
        </Badge>
      </div>
    </div>;
}
function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="rounded-none border border-border bg-card p-6 shadow-xs animate-pulse">
            <div className="aspect-[16/10] w-full bg-muted" />
            <div className="mt-4 h-6 w-3/4 bg-muted" />
            <div className="mt-2 h-4 w-1/2 bg-muted" />
            <div className="mt-6 h-20 w-full bg-muted" />
            <div className="mt-6 h-10 w-full bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-none border border-destructive/20 bg-destructive/5 p-8 text-center">
        <p className="text-sm font-medium text-foreground">
          Unable to load commercial equipment specifications.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4 rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background">
          Retry connection
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="rounded-none border border-border bg-muted/40 p-12 text-center">
        <p className="text-base font-medium text-foreground">
          No commercial units are listed currently in this category.
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Contact our logistics desk for upcoming warehouse arrival manifests.
        </p>
        <div className="mt-6">
          <Button asChild variant="outline" className="rounded-none">
            <Link to="/ContactPage">Contact technical sales</Link>
          </Button>
        </div>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const skuCode = product.slug ? `SKU-BULL-${product.id}-${product.slug.substring(0, 4).toUpperCase()}` : `SKU-BULL-${product.id}`;
      const isInStock = product.is_in_stock !== undefined ? Boolean(product.is_in_stock) : (product as any).stock_status !== "outofstock";
      const availability = isInStock ? "In Stock" : "Out of Stock";
      const strippedModel = product.title.replace(/\s+(New\s+)?Mechanical Bull$/i, "").trim();
      const model = strippedModel.length > 0 ? strippedModel : product.title;
      const verifiedSpecs = product.customFields ? Object.entries(product.customFields).filter(([_, val]) => val !== undefined && val !== null && String(val).trim() !== "").slice(0, 2).map(([key, val]) => {
        const readableLabel = key.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
        return {
          label: readableLabel,
          value: String(val)
        };
      }) : [];
      const hasUsablePrice = (() => {
        const isInvalid = (val: unknown) => {
          if (val === undefined || val === null) return true;
          const s = String(val).trim();
          return s === "" || s === "0" || s === "0.00" || s === "$0" || s === "$0.00";
        };
        const hasValidPrice = !isInvalid(product.price) && Number(product.price) !== 0;
        const hasValidFormatted = !isInvalid(product.formatted_price);
        const hasValidDiscounted = !isInvalid(product.formatted_discounted_price);
        return hasValidPrice || hasValidFormatted || hasValidDiscounted;
      })();
      return <Card key={product.id} data-index={i} className="group flex flex-col justify-between rounded-none border border-border bg-card p-0 transition-[border-color] duration-200 hover:border-foreground">
            <div className="flex flex-col">
              <ProductCardImage product={product} />

              <CardContent className="flex flex-col p-5">
                <div className="border-b border-border/60 pb-3">
                  <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                    {skuCode}
                  </span>
                </div>

                <Link to={product.link} className="mt-3 block">
                  <h3 className="font-serif text-2xl font-bold tracking-tight text-foreground uppercase hover:text-primary transition-colors">
                    {product.title}
                  </h3>
                </Link>

                <div className="mt-4 border border-border bg-muted/30 p-3">
                  <div className="flex items-baseline justify-between border-b border-border/60 pb-2.5">
                    <div>
                      <span className="block font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
                        Price
                      </span>
                      {hasUsablePrice ? <ProductPrice product={product} className="font-serif text-2xl font-bold tracking-tight text-foreground" /> : <span className="font-serif text-2xl font-bold tracking-tight text-foreground">
                          Price on Request
                        </span>}
                    </div>
                    <div className="text-right">
                      <span className="block font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
                        Availability
                      </span>
                      <span className="font-mono text-xs font-semibold text-foreground">
                        {availability}
                      </span>
                    </div>
                  </div>

                  <div className="mt-2.5 space-y-1.5 text-xs font-mono">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground uppercase">Condition</span>
                      <span className="font-medium text-foreground">New</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground uppercase">Model</span>
                      <span className="font-medium text-foreground text-right truncate">{model}</span>
                    </div>
                    {verifiedSpecs.map((spec, idx) => <div key={idx} className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground uppercase">{spec.label}</span>
                        <span className="font-medium text-foreground text-right">{spec.value}</span>
                      </div>)}
                  </div>
                </div>
              </CardContent>
            </div>

            <div className="border-t border-border bg-muted/10 p-5 space-y-2">
              <Button asChild className="w-full rounded-none border border-primary bg-primary py-3 font-serif text-base font-semibold tracking-wide text-primary-foreground uppercase shadow-none transition-colors hover:bg-primary/90">
                <Link to="/RequestQuotePage">
                  Enquire About This Bull
                </Link>
              </Button>

              <div className="pt-1">
                <Button asChild variant="outline" className="w-full rounded-none border-border font-serif uppercase tracking-wider text-xs hover:bg-muted">
                  <Link to={product.link}>View Details</Link>
                </Button>
              </div>
            </div>
          </Card>;
    })}
    </div>;
}
export default function Catalog() {
  return <section id="product-catalog" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col justify-between gap-6 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 bg-primary" />
              <p className="font-mono text-xs font-medium tracking-widest text-muted-foreground uppercase">
                NEW BULL INVENTORY
              </p>
            </div>
            <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl lg:text-5xl">Shop Our New Mechanical Bull Inventory</h2>
            <p className="mt-3 text-base text-muted-foreground">The fastest way to compare new machines is to look at the equipment itself. Browse the current inventory below to compare prices, availability, included equipment and the specifications published for each machine. Each listing links to a detailed product page where you can review the available information and enquire about the exact bull you are considering.</p>
          </div>
        </div>

        <div className="mt-10">
          <EcommerceProductsProvider wp_query={{
          product_cat: [16],
          per_page: 12,
          order: "desc",
          orderby: "date"
        }}>
            <ProductGridConsumer />
          </EcommerceProductsProvider>
        </div>

        <div className="mt-14 border border-border bg-muted/20 p-6">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h3 className="font-serif text-xl font-bold tracking-tight text-foreground uppercase">
                Need a Specific Equipment Configuration?
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Contact us with the model, intended use and equipment options you are looking for. We can confirm the configurations and customization options currently available for the machines in our catalog.
              </p>
            </div>
            <Button asChild variant="outline" className="rounded-none border-foreground font-serif uppercase tracking-wider text-sm hover:bg-foreground hover:text-background">
              <Link to="/RequestQuotePage">Discuss Equipment Options</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}